import React, { useState, useEffect } from 'react';
import ctd from 'country-telephone-data';
import { Box, Typography, Paper, TextField, Button, Avatar, FormControl, Select, MenuItem, Autocomplete, IconButton, Modal } from '@mui/material';
import { pageBoxSx, headerRowSx, leftTextSx, pageTitleSx, nodeDetailsSx, avatarSx, addGuardSX, bulkModalCancelSx, sectionPaperSx, avatarLargeSx, avatarBadgeSx, uploadTextSx, labelBoldSx, formInputSx, saveButtonSx, cancelButtonSx, personalSectionSx, identitySectionSx } from './guards.styles';
import { sectionTitleSx } from '../../styles/sectionTokens';
import ChevronRight from '../../components/ChevronRight';
import { createGuard, updateGuard, uploadGuardPhoto, uploadGuardDocument, setGuardActive } from '../../services/guardService';
import { getRoles } from '../../services/roleService';
import { getNodes } from '../../services/nodeService';
import { getMetadata, MetadataItem } from '../../services/metadataService';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';

const AddGuard: React.FC<{ onCancel?: () => void; onSave?: (message?: string) => void; initialGuard?: any; onDeactivate?: (name: string) => void }> = ({ onCancel, onSave, initialGuard, onDeactivate }) => {
  const [name, setName] = useState(initialGuard ? (initialGuard.name ?? `${initialGuard.firstName} ${initialGuard.lastName}`) : '');
  const initialPhone = initialGuard?.phone ?? '';
  const [countryCode, setCountryCode] = useState<string>(() => {
    const match = String(initialPhone).match(/^(\+\d{1,4})/);
  return match ? match[1] : '+91';
  });
  const [phone, setPhone] = useState(initialPhone.replace(/^(\+\d{1,4})\s?/, '') ?? '');
  const initialAssigned = initialGuard?.home_node_id ?? initialGuard?.home_node ?? (typeof initialGuard?.assignedNode === 'number' ? initialGuard.assignedNode : '');
  const [assignedNode, setAssignedNode] = useState<number | ''>(initialAssigned ?? '');
  const [nodesList, setNodesList] = useState<Array<{ id: number; name: string }>>([]);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [documentFileName, setDocumentFileName] = useState<string | null>(null);
  const [avatarIsObjectUrl, setAvatarIsObjectUrl] = useState<boolean>(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [documentTypeOptions, setDocumentTypeOptions] = useState<MetadataItem[]>([]);
  const [documentTypeId, setDocumentTypeId] = useState<number | ''>(initialGuard?.id_document_type_id ?? '');
  // Normalize time to HH:MM — the API may return HH:MM:SS which the <input type="time"> won't display
  const normalizeTime = (t?: string | null): string => {
    if (!t) return '';
    const m = String(t).match(/^(\d{2}:\d{2})/);
    return m ? m[1] : '';
  };
  const [startTime, setStartTime] = useState<string>(normalizeTime(initialGuard?.start_time));
  const [dutyHours, setDutyHours] = useState<string>(initialGuard?.duty_hours != null ? String(initialGuard.duty_hours) : '');
  const [startTimeError, setStartTimeError] = useState<string | null>(null);
  const [dutyHoursError, setDutyHoursError] = useState<string | null>(null);

  const apiBase = process.env.REACT_APP_API_HOSTNAME ?? 'http://localhost:8000';

  const normalizeUrl = (u?: string | null) => {
    if (!u) return null;
    try {
      const s = String(u);
      if (s.startsWith('http://') || s.startsWith('https://')) return s;
      return apiBase.replace(/\/$/, '') + (s.startsWith('/') ? s : `/${s}`);
    } catch (err) {
      return null;
    }
  };

  const extractFilename = (u?: string | null) => {
    if (!u) return null;
    try {
      const s = String(u);
      const parts = s.split('/');
      return parts[parts.length - 1] || null;
    } catch (err) {
      return null;
    }
  };

  const openConfirm = () => setConfirmOpen(true);
  const closeConfirm = () => setConfirmOpen(false);
  const confirmDeactivate = () => {
    (async () => {
      // attempt to deactivate via API
      try {
        const guardId = initialGuard?.id ?? initialGuard?.guard_id ?? initialGuard?.guardId ?? null;
        if (!guardId) {
          window.dispatchEvent(new CustomEvent('wualt:notification:error', { detail: { message: 'Cannot determine guard id for deactivate' } }));
          closeConfirm();
          return;
        }
        setSaving(true);
        const resp = await setGuardActive(guardId, false);
        const ok = resp?.success ?? resp?.data?.success ?? false;
        if (ok) {
          window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: resp?.message ?? 'Guard deactivated' } }));
          if (onDeactivate) onDeactivate(name);
        } else {
          window.dispatchEvent(new CustomEvent('wualt:notification:error', { detail: { message: resp?.message ?? 'Failed to deactivate guard' } }));
        }
      } catch (err: any) {
        console.error('Failed to set guard active', err);
        window.dispatchEvent(new CustomEvent('wualt:notification:error', { detail: { message: err?.message ?? 'Failed to deactivate guard' } }));
      } finally {
        setSaving(false);
        closeConfirm();
      }
    })();
  };

  const deactivateSx = {
    width: '125px',
    height: '36px',
    gap: '8px',
    opacity: 1,
    borderRadius: '10px',
    borderWidth: '1px',
    paddingTop: '8px',
    paddingRight: '12px',
    paddingBottom: '8px',
    paddingLeft: '12px',
    textTransform: 'none',
    border: '1px solid #FCA5A5',
    backgroundColor: 'transparent',
    color: '#DE606C',
    fontFamily: 'Inter, Arial, sans-serif',
    fontWeight: 500,
    fontSize: '13px',
    lineHeight: '19.5px',
    letterSpacing: '-0.08px',
    textAlign: 'center',
    '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' }
  } as any;

  useEffect(() => {
    // initialize preview and document name from initialGuard if present
    if (initialGuard) {
      const photoUrl = initialGuard?.photo_url ?? initialGuard?.data?.photo_url ?? initialGuard?.photoUrl ?? null;
      const normalized = normalizeUrl(photoUrl);
      if (normalized) {
        setAvatarPreview(normalized);
        setAvatarIsObjectUrl(false);
      }

      const docUrl = initialGuard?.id_document_url ?? initialGuard?.data?.id_document_url ?? initialGuard?.idDocumentUrl ?? null;
      const docName = initialGuard?.id_document_name ?? initialGuard?.data?.id_document_name ?? extractFilename(docUrl);
      if (docName) setDocumentFileName(docName);
    }

    return () => {
      if (avatarPreview && avatarIsObjectUrl) {
        try { URL.revokeObjectURL(avatarPreview); } catch (err) { /* ignore */ }
      }
    };
  }, []);

  // load document type options from metadata API
  useEffect(() => {
    let cancelled = false;
    getMetadata('GUARD_ID_DOC', true).then((items) => {
      if (!cancelled) setDocumentTypeOptions(items);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // load nodes for Assigned node select
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        // request a large page to retrieve all nodes (adjust if your API supports full list endpoint)
        const resp = await getNodes(1, 1000);
        if (cancelled) return;
        // only include nodes that are active
        const mapped = (resp.nodes || []).filter((n: any) => Boolean(n.active)).map((n: any) => ({ id: n.id, name: n.name }));
        setNodesList(mapped);
      } catch (err) {
        console.debug('Failed to load nodes for Assigned node select', err);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  return (
    <Box sx={pageBoxSx}>
      <Box mb={2} sx={{ position: 'relative' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button size="small" onClick={() => onCancel && onCancel()} sx={{ textTransform: 'none', p: 0, minWidth: 0, color: '#6B7280', fontSize: '13px' }}>Guards</Button>
            <Typography sx={{ color: '#6B7280' }}><ChevronRight /></Typography>
            <Typography sx={{ fontSize: '13px', color: '#6B7280' }}>{initialGuard ? (name || 'Guard') : 'Add Guard'}</Typography>
          </Box>
          <Box sx={headerRowSx}>
            <Box sx={leftTextSx}>
              <Typography sx={pageTitleSx}>{initialGuard ? (name || 'Add guard') : 'Add guard'}</Typography>
            </Box>
          </Box>
        </Box>
        {formError && (
          <Box sx={{ position: 'absolute', right: 0, top: 0, width: '356px', height: '54px', borderRadius: '8px', backgroundColor: '#FEF2F2', border: '1px solid #FCA5A5', display: 'flex', alignItems: 'center', px: 2, zIndex: 20 }}>
            <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: '#DE606C', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, flexShrink: 0 }}>
              <svg width="10" height="10" viewBox="0 0 8 8" fill="none"><path d="M1 1L7 7M7 1L1 7" stroke="#FFFFFF" strokeWidth="1.2" strokeLinecap="round" /></svg>
            </Box>
            <Typography sx={{ flex: 1, fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '13px', lineHeight: '19.5px', letterSpacing: '-0.08px', color: '#374151' }}>{formError}</Typography>
            <Box component="span" onClick={() => setFormError(null)} sx={{ cursor: 'pointer', color: '#9CA3AF', fontSize: 18, lineHeight: 1, ml: 1 }}>×</Box>
          </Box>
        )}
      </Box>

  <Paper sx={{ ...(sectionPaperSx as any), ...(personalSectionSx as any), mb: 3 }}>
        <Typography sx={{ fontWeight: 700, mb: 1 }}>Personal Details</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Box sx={{ position: 'relative', width: '64px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', bottom:'30px' }}>
            {/* KAN-139: always render the icon as children so MUI Avatar falls back
                to it when the image fails to load (broken/expired S3 URL), not just
                when the URL is missing. */}
            <Avatar sx={avatarLargeSx} src={avatarPreview ?? undefined}><PersonOutlineIcon sx={{ fontSize: 32, color: '#9CA3AF' }} /></Avatar>
            {/* hidden file input for avatar */}
    <input id="avatar-input" type="file" accept="image/*" style={{ display: 'none' }} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const f = e.target.files && e.target.files[0];
              if (f) {
                const url = URL.createObjectURL(f);
                // revoke previous object URL if present
                if (avatarPreview && avatarIsObjectUrl) {
                  try { URL.revokeObjectURL(avatarPreview); } catch (err) { /* ignore */ }
                }
                setPhotoFile(f);
                setAvatarPreview(url);
                setAvatarIsObjectUrl(true);
              } else {
                // if clearing file input, don't revoke server URL
                if (avatarIsObjectUrl && avatarPreview) {
                  try { URL.revokeObjectURL(avatarPreview); } catch (err) { /* ignore */ }
                }
                setPhotoFile(null);
                setAvatarPreview(null);
                setAvatarIsObjectUrl(false);
              }
            }} />
            <label htmlFor="avatar-input" style={{ position: 'absolute', right: 0, bottom: '15px', cursor: 'pointer' }}>
              <Box sx={avatarBadgeSx}>
                <CameraAltIcon sx={{ width: 11, height: 11, color: '#FFFFFF' }} />
              </Box>
            </label>
            <label htmlFor="avatar-input" style={{ cursor: 'pointer' }}>
              <Typography sx={uploadTextSx}>Upload</Typography>
            </label>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={labelBoldSx}>Name *</Typography>
            <TextField fullWidth size="small" placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} sx={{ ...formInputSx, mb: 2 }} />
            <Typography sx={labelBoldSx}>Phone number *</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Autocomplete
                options={ctd.allCountries as any[]}
                getOptionLabel={(c: any) => {
                  const rawName = c.name ?? c[0] ?? String(c);
                  const cleanName = String(rawName).replace(/\s*\(.*\)\s*$/, '');
                  return `${cleanName} (+${c.dialCode})`;
                }}
                value={ctd.allCountries.find((c: any) => c.dialCode && `+${c.dialCode}` === countryCode) ?? null}
                onChange={(_e, c: any) => setCountryCode(c?.dialCode ? `+${c.dialCode}` : '')}
                size="small"
                sx={{ width: '200px' }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Country" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', height: '41.5px' } }} />
                )}
              />
              <TextField fullWidth size="small" placeholder="7700 900000" value={phone} onChange={(e) => setPhone(e.target.value)} sx={formInputSx} />
            </Box>
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ ...(sectionPaperSx as any), ...(identitySectionSx as any), mb: 5 }}>
        <Typography sx={{ fontWeight: 700, mb: 1 }}>Identity & Assignment</Typography>
        <Box display="flex" flexDirection="column" gap={2}>
          <Typography sx={labelBoldSx}>Document Type</Typography>
          <FormControl size="small" fullWidth>
            <Select value={documentTypeId} onChange={(e) => setDocumentTypeId(e.target.value as number | '')} displayEmpty sx={{ '& .MuiSelect-select': { padding: '11px' }, borderRadius: '10px' }}>
              <MenuItem value="">Select document type</MenuItem>
              {documentTypeOptions.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography sx={labelBoldSx}>ID Document</Typography>
            <Button variant="outlined" component="label" disabled={!documentTypeId} sx={{ textTransform: 'none', width: '100%', justifyContent: 'flex-start', border: '1px solid #D1D5DB', borderRadius: '10px', opacity: documentTypeId ? 1 : 0.5, pointerEvents: documentTypeId ? 'auto' : 'none' }}>
            Upload ID document
            <input hidden accept="image/*,.pdf" type="file" onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const f = e.target.files && e.target.files[0];
              setDocumentFile(f ?? null);
              setDocumentFileName(f ? f.name : null);
            }} />
          </Button>
          <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: '11px', color: '#9CA3AF' }}>Accepts: JPG, PNG, PDF</Typography>
          {documentFileName && (
            <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '13px', lineHeight: '5.8px', color: '#374151' }}>{documentFileName}</Typography>
          )}
          <Typography sx={labelBoldSx}>Assigned node *</Typography>
          <FormControl size="small" fullWidth sx={{ mb: 2 }}>
            <Select value={assignedNode} onChange={(e) => {
              const v = String((e.target as any).value);
              setAssignedNode(v === '' ? '' : Number(v));
            }} displayEmpty sx={{ '& .MuiSelect-select': { padding: '11px' }, borderRadius: '10px' }}>
              {/* KAN-208: the field title already reads "Assigned node *"; the
                  dropdown's own placeholder should prompt the action, not repeat
                  the label. */}
              <MenuItem value="">Select node</MenuItem>
              {nodesList.map((n) => (
                <MenuItem key={n.id} value={n.id}>{n.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Duty Schedule section */}
      <Paper sx={{ ...(sectionPaperSx as any), mb: 5 }}>
        <Typography sx={{ fontWeight: 700, mb: 1 }}>Duty Schedule</Typography>
        <Box display="flex" gap={3} sx={{ mb: 2 }}>
          <Box flex={1}>
            <Typography sx={labelBoldSx}>Start Time</Typography>
            <TextField
              fullWidth
              size="small"
              type="time"
              value={startTime}
              onChange={(e) => {
                setStartTime(e.target.value);
                if (startTimeError) setStartTimeError(null);
              }}
              error={Boolean(startTimeError)}
              helperText={startTimeError ?? ''}
              inputProps={{ step: 60 }}
              sx={{ ...formInputSx, mt: 0.5 }}
            />
          </Box>
          <Box flex={1}>
            <Typography sx={labelBoldSx}>Duty Hours</Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder="e.g. 8"
              value={dutyHours}
              onChange={(e) => {
                setDutyHours(e.target.value);
                if (dutyHoursError) setDutyHoursError(null);
              }}
              error={Boolean(dutyHoursError)}
              helperText={dutyHoursError ?? ''}
              inputProps={{ min: 0, max: 24, step: 0.5 }}
              sx={{ ...formInputSx, mt: 0.5 }}
            />
          </Box>
        </Box>
      </Paper>

      {/* Status & Actions - shown only when editing an existing guard */}
      {initialGuard && (
        <Paper sx={{ ...(sectionPaperSx as any), height: '152px', mb: 3 }}>
          <Typography sx={{ ...(sectionTitleSx as any), mb: 1 }}>Status & Actions</Typography>

          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', width: '100%', mt: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '316.75px' }}>
              <Typography sx={{ fontWeight: 500, fontSize: '13px', lineHeight: '20px', color: '#374151' }}>Account status</Typography>
              <Typography sx={{ fontWeight: 400, fontSize: '12px', lineHeight: '18px', color: '#9CA3AF', mt: 1 }}>Deactivated guards cannot log in to the Responder App.</Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', backgroundColor: '#ECFDF5', borderRadius: '9999px', px: '10px', py: '6px' }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '9999px', backgroundColor: '#60AF97', mr: 1 }} />
                <Typography sx={{ fontWeight: 500, fontSize: 11, lineHeight: '16px', color: '#60AF97' }}>Active</Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 3 }}>
            <RefreshIcon sx={{ width: 13, height: 13, color: '#374151' }} />
            <Typography sx={{ fontWeight: 500, fontSize: 13, color: '#374151', cursor: 'pointer' }}>Resend first-login SMS</Typography>
          </Box>
        </Paper>
      )}

      <Box sx={{ width: '100%', maxWidth: '900px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
          {initialGuard ? (
            <Button variant="text" sx={deactivateSx} onClick={openConfirm}>Deactivate guard</Button>
          ) : <div />}
        </Box>
        <Box>
          <Button
            sx={{ ...saveButtonSx, mr: 2 }}
            disabled={saving || !name.trim() || !phone.trim() || assignedNode === ''}
            onClick={async () => {
            // Validate startTime and dutyHours before saving
            let hasFieldErrors = false;
            const normalizedTime = normalizeTime(startTime);
            if (normalizedTime) {
              const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;
              if (!timeRegex.test(normalizedTime)) {
                setStartTimeError('Please enter a valid time (HH:MM)');
                hasFieldErrors = true;
              } else {
                setStartTimeError(null);
              }
            } else {
              setStartTimeError(null);
            }
            if (dutyHours !== '') {
              const parsed = Number(dutyHours);
              if (isNaN(parsed) || parsed < 0 || parsed > 24) {
                setDutyHoursError('Duty hours must be a number between 0 and 24');
                hasFieldErrors = true;
              } else {
                setDutyHoursError(null);
              }
            } else {
              setDutyHoursError(null);
            }
            if (hasFieldErrors) return;

            // Save flow: validate and call create guard API, then upload photo/doc if present
            const payload: any = {
              name: name.trim(),
              phone: `${countryCode} ${phone.trim()}`,
              email: (initialGuard?.email) ?? undefined,
              home_node_id: assignedNode || undefined,
              document_type_id: documentTypeId || undefined,
              start_time: normalizedTime || undefined,
              duty_hours: dutyHours !== '' ? Number(dutyHours) : undefined,
            };

            setSaving(true);
            try {
              // find guard role id
              const roles = await getRoles();
              let guardRoleId: number | null = null;
              for (const r of roles) {
                const n = String(r.name || r.role || r.slug || '').toLowerCase();
                if (n.includes('guard')) { guardRoleId = r.id; break; }
              }
              if (guardRoleId) payload.userRoleId = guardRoleId;

              let resp: any = null;
              let created: any = null;
              let guardId: number | null = null;
              let successMessage = 'Guard saved successfully';

              if (initialGuard) {
                // update existing guard
                guardId = initialGuard?.id ?? initialGuard?.guard_id ?? initialGuard?.guardId ?? null;
                if (!guardId) {
                  setFormError('Cannot determine guard id for update');
                  setSaving(false);
                  return;
                }
                resp = await updateGuard(guardId, payload);
                if (resp?.success === false) {
                  setFormError(resp?.message ?? 'Failed to update guard');
                  setSaving(false);
                  return;
                }
                created = resp?.data ?? resp;
                successMessage = resp?.message ?? resp?.data?.message ?? 'Guard updated successfully';
                // if no files to upload, return success immediately
                if (!photoFile && !documentFile) {
                  window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: successMessage } }));
                  setSaving(false);
                  if (onSave) onSave(successMessage);
                  return;
                }
              } else {
                // create new guard
                resp = await createGuard(payload);
                if (resp?.success === false) {
                  setFormError(resp?.message ?? 'Failed to create guard');
                  setSaving(false);
                  return;
                }
                created = resp?.data ?? resp;
                successMessage = resp?.message ?? resp?.data?.message ?? 'Guard created successfully';
                // If no files to upload, just show success and navigate back. Don't require an id in that case.
                if (!photoFile && !documentFile) {
                  window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: successMessage } }));
                  setSaving(false);
                  if (onSave) onSave(successMessage);
                  return;
                }
                // When uploads are required, attempt to extract the created guard id from several possible shapes
                guardId = created?.id ?? created?.guard?.id ?? created?.guard_id ?? created?.guardId ?? resp?.guard_id ?? resp?.data?.guard_id ?? null;
                if (!guardId) {
                  setFormError(resp?.message ?? 'Failed to create guard (no id returned)');
                  setSaving(false);
                  return;
                }
              }

              // Otherwise attempt uploads; failures produce warning but do not block navigation
              // For the create flow (no initialGuard), mark uploads as part of the
              // initial creation so the backend doesn't write extra audit rows
              // beyond the single 'create' entry (KAN-143).
              const isInitialCreate = !initialGuard;
              let photoOk = true;
              let docOk = true;
              let photoUrl: string | null = null;
              let docUrl: string | null = null;

              if (photoFile) {
                try {
                  const upr = await uploadGuardPhoto(guardId, photoFile, { initial: isInitialCreate });
                  photoUrl = upr?.data?.photo_url ?? upr?.photo_url ?? null;
                } catch (err: any) {
                  console.error('Photo upload failed', err);
                  photoOk = false;
                }
              }

              if (documentFile) {
                try {
                  const upr = await uploadGuardDocument(guardId, documentFile, { initial: isInitialCreate });
                  docUrl = upr?.data?.id_document_url ?? upr?.id_document_url ?? null;
                } catch (err: any) {
                  console.error('Document upload failed', err);
                  docOk = false;
                }
              }

              if (photoOk && docOk) {
                window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: successMessage } }));
              } else {
                const parts: string[] = [];
                if (!photoOk) parts.push('photo');
                if (!docOk) parts.push('ID document');
                const p = parts.join(' and ');
                const warnMsg = `Guard created, but ${p} upload failed`;
                window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: warnMsg } }));
                // prefer warning message when navigating back
                if (onSave) onSave(warnMsg);
                setSaving(false);
                // KAN-139: backend returns absolute presigned S3 URLs; use normalizeUrl
                // so absolute URLs are kept as-is instead of being prefixed with apiBase.
                if (photoUrl) setAvatarPreview(normalizeUrl(photoUrl));
                if (docUrl) setAvatarPreview((prev) => prev ?? normalizeUrl(docUrl));
                if (onSave) return;
              }

              // update any previews if URLs were returned (KAN-139: see normalizeUrl note above)
              if (photoUrl) setAvatarPreview(normalizeUrl(photoUrl));
              if (docUrl) setAvatarPreview((prev) => prev ?? normalizeUrl(docUrl));

              setSaving(false);
              if (onSave) onSave(successMessage);
            } catch (err: any) {
              console.error('Create guard failed', err);
              setFormError(err?.message ?? 'Failed to save guard');
              setSaving(false);
            }
          }}>Save</Button>
          <Button sx={cancelButtonSx} onClick={() => onCancel && onCancel()}>Cancel</Button>
        </Box>
      </Box>

      <Modal open={confirmOpen} onClose={closeConfirm}>
        <Box sx={{ position: 'absolute', width: '400px', height: '229.6953125px', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', borderRadius: '16px', backgroundColor: '#FFFFFF', boxShadow: '0 10px 30px rgba(0,0,0,0.12)', outline: 'none', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ width: '100%', height: '65px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(0,0,0,0.06)', px: '24px' }}>
            <Typography sx={{ fontWeight: 600, fontSize: '16px' }}>Deactivate guard?</Typography>
            <IconButton size="small" onClick={closeConfirm} sx={{ width: '13px', height: '13px', padding: 0 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </IconButton>
          </Box>
          <Box sx={{ width: '100%', height: '164.6953125px', gap: '20px', px: '24px', pt: '20px', pb: '20px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <Box sx={{ width: '352px', height: '67.1953125px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <Box sx={{ width: '80px', height: '36px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', backgroundColor: '#FEF2F2' }}>
                <Box component="span" sx={{ width: '16px', height: '16px' }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 3.33333V6.66667" stroke="#DE606C" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/><path d="M8 10.6667H8.00667" stroke="#DE606C" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </Box>
              </Box>
              <Box>
                <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '22.4px', color: 'text.primary' }}>Deactivated guards are hidden from active assignments. This action cannot be undone.</Typography>
              </Box>
            </Box>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button sx={{ width: '76.4921875px', height: '37.5px', borderRadius: '10px', backgroundColor: '#E5E7EB', color: '#111827', textTransform: 'none', border: '1px solid rgba(0,0,0,0.08)' }} onClick={closeConfirm}>Cancel</Button>
              <Button sx={{ width: '98.21875px', height: '35.5px', borderRadius: '10px', backgroundColor: '#DE606C', color: '#FFFFFF', textTransform: 'none' }} onClick={confirmDeactivate}>Deactivate</Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default AddGuard;
